/**
 * Subscription tier types and interfaces
 *
 * This module defines the 4-tier subscription system:
 * - FREE: Basic tier with limited features
 * - STARTER: Entry paid tier for small businesses
 * - PROFESSIONAL: Advanced tier for growing businesses
 * - ENTERPRISE: Premium tier with unlimited features
 */

/**
 * Subscription tier enum - 4 tiers from free to enterprise
 */
export type SubscriptionTier = 'free' | 'starter' | 'professional' | 'enterprise';

/**
 * Tier hierarchy for comparison operations
 * Lower number = lower tier
 */
export const TIER_ORDER: Record<SubscriptionTier, number> = {
  free: 0,
  starter: 1,
  professional: 2,
  enterprise: 3,
};

/**
 * Human-readable tier names
 */
export const TIER_NAMES: Record<SubscriptionTier, string> = {
  free: 'Free',
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

/**
 * Tier color coding for UI components
 */
export const TIER_COLORS: Record<SubscriptionTier, string> = {
  free: '#6B7280',      // gray
  starter: '#3B82F6',   // blue
  professional: '#8B5CF6', // purple
  enterprise: '#F59E0B', // gold
};

/**
 * Feature limits by subscription tier
 */
export interface TierLimits {
  maxProducts: number;    // -1 for unlimited
  maxLocations: number;   // -1 for unlimited
  maxSales: number;       // Monthly sales limit, -1 for unlimited
  hasB2BAccess: boolean;
  hasAnalytics: boolean;
  reportExportLevel: 'none' | 'basic' | 'advanced';
  supportLevel: 'community' | 'email' | 'priority' | 'dedicated';
}

/**
 * Default limits for each tier
 */
export const DEFAULT_TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  free: {
    maxProducts: 50,
    maxLocations: 1,
    maxSales: 100,
    hasB2BAccess: false,
    hasAnalytics: false,
    reportExportLevel: 'none',
    supportLevel: 'community',
  },
  starter: {
    maxProducts: 500,
    maxLocations: 5,
    maxSales: 1000,
    hasB2BAccess: false,
    hasAnalytics: false,
    reportExportLevel: 'basic',
    supportLevel: 'email',
  },
  professional: {
    maxProducts: 2000,
    maxLocations: 20,
    maxSales: 5000,
    hasB2BAccess: true,
    hasAnalytics: true,
    reportExportLevel: 'advanced',
    supportLevel: 'priority',
  },
  enterprise: {
    maxProducts: -1, // unlimited
    maxLocations: -1, // unlimited
    maxSales: -1, // unlimited
    hasB2BAccess: true,
    hasAnalytics: true,
    reportExportLevel: 'advanced',
    supportLevel: 'dedicated',
  },
};

/**
 * Feature flags by tier
 */
export interface TierFeatures {
  products: string;
  locations: string;
  monthlySales: string;
  b2bCustomers: boolean;
  advancedAnalytics: boolean;
  reportExports: string;
  supportLevel: string;
}

/**
 * Feature comparison matrix for display
 */
export const TIER_FEATURES: Record<SubscriptionTier, TierFeatures> = {
  free: {
    products: '50',
    locations: '1',
    monthlySales: '100',
    b2bCustomers: false,
    advancedAnalytics: false,
    reportExports: 'None',
    supportLevel: 'Community',
  },
  starter: {
    products: '500',
    locations: '5',
    monthlySales: '1,000',
    b2bCustomers: false,
    advancedAnalytics: false,
    reportExports: 'Basic',
    supportLevel: 'Email',
  },
  professional: {
    products: '2,000',
    locations: '20',
    monthlySales: '5,000',
    b2bCustomers: true,
    advancedAnalytics: true,
    reportExports: 'Advanced',
    supportLevel: 'Priority',
  },
  enterprise: {
    products: 'Unlimited',
    locations: 'Unlimited',
    monthlySales: 'Unlimited',
    b2bCustomers: true,
    advancedAnalytics: true,
    reportExports: 'Advanced',
    supportLevel: 'Dedicated',
  },
};

/**
 * Upgrade modal scenarios
 */
export type UpgradeScenario =
  | 'second_location'
  | 'b2b_customer'
  | 'report_export'
  | 'analytics';

/**
 * Scenario configuration for upgrade prompts
 */
export interface ScenarioConfig {
  title: string;
  message: string;
  requiredTier: SubscriptionTier;
  featureName: string;
}

/**
 * Predefined upgrade scenarios
 */
export const UPGRADE_SCENARIOS: Record<UpgradeScenario, ScenarioConfig> = {
  second_location: {
    title: 'Multiple Locations',
    message: 'Upgrade to Starter to manage multiple locations for your business.',
    requiredTier: 'starter',
    featureName: 'Multiple Locations',
  },
  b2b_customer: {
    title: 'B2B Customers',
    message: 'Upgrade to Professional to access B2B customer management and wholesale pricing.',
    requiredTier: 'professional',
    featureName: 'B2B Customers',
  },
  report_export: {
    title: 'Report Exports',
    message: 'Upgrade to export detailed reports and analytics for your business.',
    requiredTier: 'starter',
    featureName: 'Report Exports',
  },
  analytics: {
    title: 'Advanced Analytics',
    message: 'Upgrade to Professional for forecasting, trends, and advanced business insights.',
    requiredTier: 'professional',
    featureName: 'Advanced Analytics',
  },
};

/**
 * Helper function to check if current tier has access to required tier
 * @param currentTier - User's current subscription tier
 * @param requiredTier - Minimum tier required for feature
 * @returns true if current tier >= required tier
 */
export const hasTierAccess = (
  currentTier: SubscriptionTier,
  requiredTier: SubscriptionTier
): boolean => {
  return TIER_ORDER[currentTier] >= TIER_ORDER[requiredTier];
};

/**
 * Helper function to check if a limit value is unlimited
 * @param value - Limit value to check
 * @returns true if value is -1 (unlimited)
 */
export const isUnlimited = (value: number): boolean => {
  return value === -1;
};

/**
 * Get tier limits for a specific tier
 * @param tier - Subscription tier
 * @returns Tier limits configuration
 */
export const getTierLimits = (tier: SubscriptionTier): TierLimits => {
  return DEFAULT_TIER_LIMITS[tier];
};

/**
 * Get tier features for display
 * @param tier - Subscription tier
 * @returns Tier features configuration
 */
export const getTierFeatures = (tier: SubscriptionTier): TierFeatures => {
  return TIER_FEATURES[tier];
};
